import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Loader2, Undo2, CalendarX2, Link2, AlertTriangle, CheckCircle2, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const n = (v) => (v || 0).toLocaleString();
const money = (v) => `$${Math.round(v || 0).toLocaleString()}`;
const CLAIMS_REASON = 'Pre-inception legacy claims — date of loss before the policy incepted, $0 paid, no reserve, closed. Owner ruling 9 Oct 2026: disposition out of the book.';
const TEST_REASON = 'Exercise / test traffic — non-broker intake before the live/test split (pipeline kicks, mailbox and webhook probes, demo seeds, Unknown-applicant stubs). Owner instruction 22 Oct 2026: off every figure, kept on file.';
const LINK_REASON = 'Legacy policies linked to the entity register by exact business-name match only, flagged name_match_unverified. Owner ruling 9 Oct 2026.';

function Stat({ label, value, color, testid }) {
  return (
    <div className="px-3 py-2 rounded-sm" style={{ background: 'rgba(136,150,168,0.07)' }} data-testid={testid}>
      <p className="text-xl font-bold font-mono leading-none" style={{ color: color || C.navy }}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider mt-1" style={{ color: C.slate }}>{label}</p>
    </div>
  );
}

const RunButton = ({ onClick, busy, color, children, testid, disabled }) => (
  <button onClick={onClick} disabled={busy || disabled} data-testid={testid}
    className="px-3 py-1.5 rounded-sm text-[11px] font-semibold text-white disabled:opacity-50 flex items-center gap-1.5" style={{ background: color }}>
    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}{children}
  </button>
);

export const BookCleanupSection = ({ api }) => {
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [claims, setClaims] = useState(null);
  const [links, setLinks] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [testReason, setTestReason] = useState(TEST_REASON);
  const [armed, setArmed] = useState('');            // which destructive button awaits its second click
  const [rollbackReason, setRollbackReason] = useState('');
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState('');
  const [claimsReason, setClaimsReason] = useState(CLAIMS_REASON);
  const [linkReason, setLinkReason] = useState(LINK_REASON);

  const load = useCallback(() => {
    api.get('/migration/book-cleanup/pre-inception/scan').then(r => setClaims(r.data)).catch(() => {});
    api.get('/migration/book-cleanup/entity-links/scan').then(r => setLinks(r.data)).catch(() => {});
    api.get('/migration/book-cleanup/test-traffic/scan').then(r => setTraffic(r.data)).catch(() => {});
    api.get('/migration/book-cleanup/runs?limit=10').then(r => setRuns(r.data.runs || [])).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, path, body, ok) => {
    setArmed('');
    setBusy(key);
    try { const { data } = await api.post(path, body); toast.success(ok(data)); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'The run did not complete'); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-4" data-testid="book-cleanup-section">
      <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="pre-inception-card">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <CalendarX2 className="w-4 h-4" style={{ color: C.red }} />
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Pre-inception legacy claims</h3>
          {claims && (
            <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" data-testid="pre-inception-count"
              style={{ background: claims.records ? '#FDF1F1' : '#E7F6EF', color: claims.records ? C.red : C.green }}>
              {claims.records ? `${n(claims.records)} to disposition` : 'none on the book'}
            </span>
          )}
        </div>
        {!claims ? <p className="text-[11px]" style={{ color: C.slate }}>Scanning…</p> : (
          <>
            <p className="text-[11px] mb-3" style={{ color: C.slate }}>{claims.note}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              <Stat label="Dispositionable" value={n(claims.records)} color={claims.records ? C.red : C.green} testid="pi-records" />
              <Stat label="Already off the book" value={n(claims.already_dispositioned)} testid="pi-done" />
              <Stat label="Exceptions for a human" value={n(claims.exception_count)} color={claims.exception_count ? C.amber : C.green} testid="pi-exceptions" />
              {claims.days_before_inception && <Stat label="Days before inception (median)" value={n(claims.days_before_inception.median)} testid="pi-days" />}
            </div>
            {Object.keys(claims.by_status).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="pi-by-status">
                {Object.entries(claims.by_status).map(([k, v]) => <span key={k} className="px-2 py-1 rounded-sm text-[10px] font-mono" style={{ background: 'rgba(226,75,74,0.07)', color: C.red }}>{k} · {v}</span>)}
              </div>
            )}
            {claims.exceptions.length > 0 && (
              <div className="mb-3 p-3 rounded-sm" style={{ background: '#FDF7EC', borderLeft: `3px solid ${C.amber}` }} data-testid="pi-exception-list">
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: C.amber }}>Left alone — carries money or is still open; a human decides these</p>
                {claims.exceptions.slice(0, 6).map(e => (
                  <p key={e.claim_id} className="text-[11px]" style={{ color: C.slate }}>
                    <span className="font-mono" style={{ color: C.navy }}>{e.policy_number}</span> · {e.status} · paid {money(e.paid)} · reserve {money(e.reserve)} · {e.days_before_inception}d before inception · {e.reason}
                  </p>
                ))}
                {claims.exceptions.length > 6 && <p className="text-[10px]" style={{ color: C.slate }}>…and {claims.exception_count - 6} more</p>}
              </div>
            )}
            {isAdmin && claims.records > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Input value={claimsReason} onChange={e => setClaimsReason(e.target.value)} className="h-8 text-[11px] rounded-sm flex-1 min-w-[280px]" data-testid="pi-reason-input" />
                <RunButton color={C.red} busy={busy === 'claims'} disabled={claimsReason.trim().length < 10} testid="pi-apply-btn"
                  onClick={() => run('claims', '/migration/book-cleanup/pre-inception/apply', { reason: claimsReason }, d => `${d.claims} claims off the book — rollback kept on run ${d.run_id.slice(0, 8)}`)}>
                  Disposition {n(claims.records)} claim(s)
                </RunButton>
              </div>
            )}
          </>
        )}
      </Card>

      <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="entity-links-card">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <Link2 className="w-4 h-4" style={{ color: C.blue }} />
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Legacy policies with no entity record</h3>
          {links && (
            <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" data-testid="entity-links-count"
              style={{ background: links.linkable.policies ? '#E7F6EF' : 'rgba(136,150,168,0.12)', color: links.linkable.policies ? C.green : C.slate }}>
              {links.linkable.policies ? `${n(links.linkable.policies)} linkable by exact name` : 'nothing links by exact name'}
            </span>
          )}
        </div>
        {!links ? <p className="text-[11px]" style={{ color: C.slate }}>Scanning…</p> : (
          <>
            <p className="text-[11px] mb-3" style={{ color: C.slate }}>{links.note}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              <Stat label="Policies" value={n(links.records)} testid="el-records" />
              <Stat label="Premium" value={money(links.gwp)} testid="el-gwp" />
              <Stat label="Distinct names" value={n(links.distinct_names)} testid="el-names" />
              <Stat label="Linkable (exact, unique)" value={n(links.linkable.policies)} color={C.green} testid="el-linkable" />
              <Stat label="Ambiguous" value={n(links.ambiguous.policies)} color={links.ambiguous.policies ? C.amber : C.slate} testid="el-ambiguous" />
              <Stat label="No entity match" value={n(links.unmatched.policies)} color={C.slate} testid="el-unmatched" />
              <Stat label="Already linked by name" value={n(links.already_linked)} testid="el-done" />
            </div>
            <p className="text-[11px] mb-3 flex items-center gap-1.5" style={{ color: C.navy }} data-testid="el-policyholder-note">
              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: C.green }} />
              {n(links.policyholder_backed.policies)} of these carry a policyholder record — {n(links.policyholder_backed.resolved)} of {n(links.policyholder_backed.distinct_policyholders)} resolve on the policyholder register
              {links.policyholder_backed.unresolved ? `, ${n(links.policyholder_backed.unresolved)} do not` : ''}. They stay on the book as legacy insureds and are counted as such.
            </p>
            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2">
                <Input value={linkReason} onChange={e => setLinkReason(e.target.value)} className="h-8 text-[11px] rounded-sm flex-1 min-w-[280px]" data-testid="el-reason-input" />
                <RunButton color={C.blue} busy={busy === 'links'} disabled={!links.linkable.policies || linkReason.trim().length < 10} testid="el-apply-btn"
                  onClick={() => run('links', '/migration/book-cleanup/entity-links/apply', { reason: linkReason }, d => `${d.policies} policies linked (flagged name_match_unverified)`)}>
                  Link {n(links.linkable.policies)} by exact name
                </RunButton>
              </div>
            )}
          </>
        )}
      </Card>

      <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="test-traffic-card">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <FlaskConical className="w-4 h-4" style={{ color: C.amber }} />
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Exercise / test traffic on the live book</h3>
          {traffic && (
            <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" data-testid="test-traffic-count"
              style={{ background: (traffic.records || traffic.orphan_pipeline_runs || traffic.orphan_email_intake) ? '#FDF7EC' : '#E7F6EF', color: (traffic.records || traffic.orphan_pipeline_runs || traffic.orphan_email_intake) ? C.amber : C.green }}>
              {traffic.records ? `${n(traffic.records)} to disposition` : (traffic.orphan_pipeline_runs || traffic.orphan_email_intake) ? `${n(traffic.orphan_pipeline_runs + traffic.orphan_email_intake)} orphan row(s) to disposition` : 'none on the book'}
            </span>
          )}
        </div>
        {!traffic ? <p className="text-[11px]" style={{ color: C.slate }}>Scanning…</p> : (
          <>
            <p className="text-[11px] mb-3" style={{ color: C.slate }}>{traffic.note}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              <Stat label="Submissions" value={n(traffic.records)} color={traffic.records ? C.amber : C.green} testid="tt-records" />
              <Stat label="Policies they bound" value={`${n(traffic.bound_policies.records)} · ${money(traffic.bound_policies.premium)}`} testid="tt-policies" />
              <Stat label="Orphan pipeline runs" value={n(traffic.orphan_pipeline_runs)} testid="tt-orphan-runs" />
              <Stat label="Orphan email intake" value={n(traffic.orphan_email_intake)} testid="tt-orphan-intake" />
              <Stat label="Already off the book" value={n(traffic.already_dispositioned)} testid="tt-done" />
              <Stat label="Broker-raised, left alone" value={n(traffic.left_alone_broker_raised)} color={C.green} testid="tt-broker-rows" />
            </div>
            {Object.keys(traffic.by_channel).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="tt-by-channel">
                {Object.entries(traffic.by_channel).map(([k, v]) => <span key={k} className="px-2 py-1 rounded-sm text-[10px] font-mono" style={{ background: 'rgba(232,160,32,0.08)', color: '#A8720E' }}>{k} {n(v)}</span>)}
                {Object.entries(traffic.cascade).map(([k, v]) => <span key={k} className="px-2 py-1 rounded-sm text-[10px] font-mono" style={{ background: 'rgba(136,150,168,0.1)', color: C.slate }}>{k} {n(v)}</span>)}
              </div>
            )}
            {isAdmin && (traffic.records > 0 || traffic.orphan_pipeline_runs > 0 || traffic.orphan_email_intake > 0) && (
              <div className="flex flex-wrap items-center gap-2">
                <Input value={testReason} onChange={e => setTestReason(e.target.value)} className="h-8 text-[11px] rounded-sm flex-1 min-w-[280px]" data-testid="tt-reason-input" />
                <RunButton color={armed === 'traffic' ? C.red : C.amber} busy={busy === 'traffic'} disabled={testReason.trim().length < 10} testid="tt-apply-btn"
                  onClick={() => armed === 'traffic'
                    ? run('traffic', '/migration/book-cleanup/test-traffic/apply', { reason: testReason }, d => `${d.submissions} submissions and ${d.policies} policies off the book — rollback kept on run ${d.run_id.slice(0, 8)}`)
                    : setArmed('traffic')}>
                  {armed === 'traffic'
                    ? `Confirm — ${n(traffic.records)} submission(s), ${n(traffic.bound_policies.records)} policies, ${n(traffic.orphan_pipeline_runs + traffic.orphan_email_intake)} orphan row(s) off the book`
                    : `Disposition ${n(traffic.records)} submission(s)${traffic.orphan_pipeline_runs || traffic.orphan_email_intake ? ` + ${n(traffic.orphan_pipeline_runs + traffic.orphan_email_intake)} orphan row(s)` : ''}`}
                </RunButton>
                {armed === 'traffic' && <button onClick={() => setArmed('')} className="text-[10px] underline" style={{ color: C.slate }} data-testid="tt-cancel-btn">cancel</button>}
              </div>
            )}
          </>
        )}
      </Card>

      {runs.length > 0 && (
        <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="book-cleanup-runs">
          <h3 className="text-sm font-semibold mb-3" style={{ color: C.navy }}>Book cleanup runs</h3>
          {runs.map(r => (
            <div key={r.run_id} className="flex flex-wrap items-center gap-3 py-1.5 border-t text-[11px]" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`bc-run-${r.run_id}`}>
              <span className="font-mono" style={{ color: C.slate }}>{String(r.created_at).slice(0, 16).replace('T', ' ')}</span>
              <span style={{ color: C.navy }}>{r.kind === 'pre_inception_claims_disposition' ? 'Pre-inception claims dispositioned' : r.kind === 'test_traffic_disposition' ? 'Exercise / test traffic dispositioned' : 'Legacy policies linked by name'}</span>
              <span className="font-mono" style={{ color: C.navy }}>{n(r.kind === 'test_traffic_disposition' ? Object.values(r.counts || {}).reduce((a, b) => a + b, 0) - (r.counts?.orphan_pipeline_runs || 0) - (r.counts?.orphan_email_intake || 0) : r.records)} record(s){r.kind === 'test_traffic_disposition' && r.counts?.orphan_pipeline_runs ? ` · ${n(r.counts.orphan_pipeline_runs)} orphan run(s)` : ''}</span>
              <span style={{ color: C.slate }}>{r.scope.replace(/_/g, ' ')} · by {r.by || 'unknown'}</span>
              {r.rolled_back ? (
                <span className="px-2 py-0.5 rounded-sm text-[9px] font-bold uppercase" style={{ background: 'rgba(136,150,168,0.15)', color: C.slate }} data-testid={`bc-run-rolledback-${r.run_id}`}>rolled back</span>
              ) : isAdmin && (
                <span className="ml-auto flex items-center gap-1.5">
                  {armed === r.run_id && (
                    <Input value={rollbackReason} onChange={e => setRollbackReason(e.target.value)} placeholder="Why roll this run back? (10+ characters)"
                      className="h-7 text-[10px] rounded-sm w-72" data-testid={`bc-rollback-reason-${r.run_id}`} autoFocus />
                  )}
                  <button onClick={() => armed === r.run_id
                      ? run(r.run_id, `/migration/book-cleanup/rollback/${r.run_id}`, { reason: rollbackReason }, d => `${d.restored} record(s) restored exactly`)
                      : (setArmed(r.run_id), setRollbackReason(''))}
                    disabled={busy === r.run_id || (armed === r.run_id && rollbackReason.trim().length < 10)}
                    className="px-2 py-0.5 rounded-sm text-[10px] font-semibold border flex items-center gap-1 disabled:opacity-50"
                    style={{ borderColor: C.red, color: C.red }} data-testid={`bc-rollback-${r.run_id}`}>
                    {busy === r.run_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}{armed === r.run_id ? 'Confirm roll back' : 'Roll back'}
                  </button>
                  {armed === r.run_id && <button onClick={() => setArmed('')} className="text-[10px] underline" style={{ color: C.slate }}>cancel</button>}
                </span>
              )}
            </div>
          ))}
          <p className="text-[10px] mt-3" style={{ color: C.slate }}>Every run keeps the before value of every field it touched, so a rollback restores each row exactly.</p>
        </Card>
      )}
    </div>
  );
};
